import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { BookUser, ClipboardCopy, MailPlus, RefreshCw, ShieldCheck, ShieldOff, Trash2, UserPlus, X, Plus, AtSign } from 'lucide-react';
import type { Contact, InviteResult, User } from '@dts/core';
import { api, errorMessage } from '@/lib/api';
import { useContacts, useMe } from '@/lib/queries';
import { ago } from '@/lib/format';
import { Avatar, Favicon } from './Avatar';
import { confirmDialog } from './Dialogs';
import { Button, Chip, cn, Empty, IconButton, Input, Modal, Textarea } from './ui';

/**
 * Settings → People: everyone with access, their addresses, and pending invitations. System administrators see
 * everyone; domain admins see the people on their domains and manage only those domains' addresses and invitations.
 */
export function PeoplePanel() {
  const qc = useQueryClient();
  const me = useMe().data;
  const isAdmin = me?.user.role === 'admin';
  const adminDomains = me?.adminDomains ?? [];
  const canManage = isAdmin || adminDomains.length > 0;
  const users = useQuery({ queryKey: ['admin-users'], queryFn: api.adminUsers, enabled: canManage });
  const addresses = useQuery({ queryKey: ['admin-addresses'], queryFn: () => api.adminAddresses(), enabled: canManage });
  const invites = useQuery({ queryKey: ['admin-invites'], queryFn: api.invites, enabled: canManage });
  const domains = useQuery({ queryKey: ['admin-domains'], queryFn: api.adminDomains, enabled: canManage });
  const [inviting, setInviting] = useState(false);
  const [addFor, setAddFor] = useState<User | null>(null);
  const refresh = () => ['admin-users', 'admin-addresses', 'admin-invites', 'admin-domains', 'me'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const byUser = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const a of addresses.data ?? []) m.set(a.userId, [...(m.get(a.userId) ?? []), a.email]);
    return m;
  }, [addresses.data]);
  const pending = new Map((invites.data ?? []).map((i) => [i.userId, i]));
  const catchAll = new Map((domains.data ?? []).filter((d) => d.catchAllUserId).map((d) => [d.name, d.catchAllUserId!]));

  if (me && !canManage) return <Empty icon={<ShieldCheck />} title="Administrators only" hint="Ask an administrator to invite people or add addresses." />;

  return (
    <div>
      <div className="mb-5 flex items-end justify-between">
        <div>
          <h2 className="text-[16px] font-semibold">People</h2>
          <p className="mt-0.5 max-w-xl text-[12.5px] text-muted">
            Invite someone and they get their own mailbox on {isAdmin ? 'any domain you host' : adminDomains.join(', ')}. They choose their own password with a one-time invite code.
            {!isAdmin && ' As a domain admin you manage the addresses, aliases and invitations for your domains.'}
          </p>
        </div>
        <Button variant="primary" icon={<UserPlus className="size-4" />} onClick={() => setInviting(true)}>Invite someone</Button>
      </div>

      <div className="space-y-2">
        {(users.data ?? []).map((u) => {
          const inv = pending.get(u.id);
          const mine = byUser.get(u.id) ?? [];
          const catchAlls = [...catchAll.entries()].filter(([, id]) => id === u.id).map(([d]) => d);
          return (
            <div key={u.id} className="glass flex items-start gap-4 rounded-2xl px-4 py-3.5">
              <Avatar name={u.displayName} address={u.email} size={38} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[14px] font-semibold">{u.displayName}</span>
                  {u.role === 'admin' && <Chip>admin</Chip>}
                  {u.disabled && <Chip color="#f43f5e">disabled</Chip>}
                  {inv && <Chip color={inv.expired ? '#f43f5e' : '#f59e0b'}>{inv.expired ? 'invite expired' : 'invited'}</Chip>}
                  {!inv && !u.mfaEnabled && <Chip color="#f59e0b">2-step not set up</Chip>}
                  {u.id === me?.user.id && <span className="text-[11.5px] text-faint">you</span>}
                </div>
                <div className="text-[12px] text-muted">Signs in as {u.email}{inv && !inv.expired ? ` · invite expires ${ago(inv.expiresUtc)}${inv.sentTo ? ` · sent to ${inv.sentTo}` : ''}` : ''}</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {mine.map((a) => (
                    <span key={a} className="group inline-flex items-center gap-1.5 rounded-full border border-line bg-bg/40 py-0.5 pl-1.5 pr-1 text-[12px]">
                      <Favicon domain={a.split('@')[1]} size={13} />
                      {a}
                      <button className="rounded-full px-1 text-faint opacity-0 transition hover:text-danger group-hover:opacity-100" title="Remove address" onClick={async () => {
                        if (!(await confirmDialog(`Remove ${a}?`, `New mail to ${a} will go to the domain's catch-all and domain admins instead.`, { confirmLabel: 'Remove', danger: true }))) return;
                        await api.removeAddress(a).catch((e) => toast.error(errorMessage(e)));
                        refresh();
                      }}><X className="size-3" /></button>
                    </span>
                  ))}
                  {catchAlls.map((d) => <span key={d} className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-[11.5px] text-accent" title="Receives mail for unknown addresses on this domain"><AtSign className="size-3" />catch-all · {d}</span>)}
                  <button className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] text-muted hover:bg-line hover:text-fg" onClick={() => setAddFor(u)}><Plus className="size-3" />Add address</button>
                </div>
              </div>
              <div className="flex shrink-0 gap-1">
                {inv && (
                  <>
                    <Button size="sm" variant="soft" icon={<RefreshCw className="size-3.5" />} onClick={async () => {
                      try {
                        const r = await api.resendInvite(u.id);
                        refresh();
                        showCode(r);
                      } catch (e) { toast.error(errorMessage(e)); }
                    }}>New code</Button>
                    <IconButton label="Cancel invitation and remove account" onClick={async () => {
                      if (!(await confirmDialog(`Cancel ${u.displayName}'s invitation?`, 'Their account and addresses are removed.', { confirmLabel: 'Cancel invitation', danger: true }))) return;
                      await api.revokeInvite(u.id, true).catch((e) => toast.error(errorMessage(e)));
                      refresh();
                    }}><Trash2 /></IconButton>
                  </>
                )}
                {isAdmin && !inv && u.mfaEnabled && (
                  <IconButton label="Reset two-step verification (lost phone)" onClick={async () => {
                    if (!(await confirmDialog(
                      `Reset ${u.displayName}'s two-step verification?`,
                      "Use this if they lost their phone. They're signed out everywhere and set up their authenticator app again at their next sign-in.",
                      { confirmLabel: 'Reset', danger: true },
                    ))) return;
                    try {
                      await api.resetUserMfa(u.id);
                      toast.success(`${u.displayName} will set up two-step verification at their next sign-in`);
                      refresh();
                    } catch (e) { toast.error(errorMessage(e)); }
                  }}><ShieldOff /></IconButton>
                )}
                {isAdmin && !inv && u.id !== me?.user.id && (
                  <Button size="sm" variant="ghost" onClick={async () => {
                    await api.updateUser(u.id, { disabled: !u.disabled }).catch((e) => toast.error(errorMessage(e)));
                    refresh();
                  }}>{u.disabled ? 'Enable' : 'Disable'}</Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <InviteDialog open={inviting} onClose={() => setInviting(false)} domains={(domains.data ?? []).map((d) => d.name)} canMakeAdmin={isAdmin} onDone={(r) => (refresh(), showCode(r))} />
      <AddAddressDialog user={addFor} onClose={() => setAddFor(null)} domains={(domains.data ?? []).map((d) => d.name)} onDone={refresh} />
      <CodeDialog />
    </div>
  );
}

// ---------- invite result ----------

const WEB_APP = 'https://email.chuckgiddens.com';

let setCodeResult: ((r: InviteResult | null) => void) | null = null;
function showCode(r: InviteResult) {
  setCodeResult?.(r);
}

function CodeDialog() {
  const [r, setR] = useState<InviteResult | null>(null);
  setCodeResult = setR;
  const pretty = r ? `${r.code.slice(0, 4)}-${r.code.slice(4)}` : '';
  const instructions = r
    ? `You're invited to DTS Mail.\n\nAccept in your browser (your code is filled in, just choose a password):\n${WEB_APP}/#invite?email=${encodeURIComponent(r.user.email)}&code=${r.code}\n\nOr use the app on your computer:\n1. Download it: https://www.chuckgiddens.com/email\n2. Open it and choose "I have an invite code".\n3. Enter ${r.user.email}, the code ${pretty}, and choose your password.\n\nThe code works once and expires on ${new Date(r.expiresUtc).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}.`
    : '';
  return (
    <Modal open={!!r} onClose={() => setR(null)} width={480} title="Invitation ready">
      {r && (
        <div className="space-y-4 p-5">
          <p className="text-[13px] text-muted">
            {r.sentTo ? <>The invitation was emailed to <b className="text-fg">{r.sentTo}</b>. You can also share the code yourself:</> : <>Share this code with <b className="text-fg">{r.user.displayName}</b> (text, call, in person):</>}
          </p>
          <div className="rounded-2xl bg-bg/60 py-5 text-center font-mono text-[30px] font-bold tracking-[0.3em]">{pretty}</div>
          <div className="text-[12.5px] text-muted">
            Signs in as <b className="text-fg">{r.user.email}</b>{r.addresses?.length ? <> · mailboxes: {r.addresses.join(', ')}</> : null}<br />
            Expires {new Date(r.expiresUtc).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} · works once
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="soft" icon={<ClipboardCopy className="size-4" />} onClick={() => (navigator.clipboard.writeText(instructions), toast.success('Instructions copied'))}>Copy instructions</Button>
            <Button variant="primary" onClick={() => setR(null)}>Done</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ---------- invite form ----------

/** Only system administrators may invite another administrator; domain admins invite people to their own domains. */
function InviteDialog({ open, onClose, domains, canMakeAdmin, onDone }: { open: boolean; onClose: () => void; domains: string[]; canMakeAdmin: boolean; onDone: (r: InviteResult) => void }) {
  const [name, setName] = useState('');
  const [local, setLocal] = useState('');
  const [domain, setDomain] = useState('');
  const [extra, setExtra] = useState('');
  const [sendTo, setSendTo] = useState('');
  const [note, setNote] = useState('');
  const [admin, setAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const d = domain || domains[0] || '';
  const address = local.trim() ? `${local.trim().toLowerCase()}@${d}` : '';

  const reset = () => {
    setName(''); setLocal(''); setExtra(''); setSendTo(''); setNote(''); setAdmin(false);
  };
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.createInvite({
        email: address,
        displayName: name.trim() || undefined,
        addresses: extra.split(/[\s,;]+/).map((x) => x.trim()).filter((x) => x.includes('@')),
        sendTo: sendTo.trim() || undefined,
        role: admin && canMakeAdmin ? 'admin' : 'user',
        note: note.trim() || undefined,
      });
      reset();
      onClose();
      onDone(r);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} width={560} title={<span className="flex items-center gap-2"><MailPlus className="size-4 text-accent" />Invite someone</span>}>
      <div className="space-y-4 p-5">
        <ContactNameInput value={name} onChange={setName} onPick={(c) => {
          setName(c.name);
          // Invite goes to an address they already read; prefer one outside the domains we host.
          const personal = c.emails.find((e) => !domains.includes(e.split('@')[1]?.toLowerCase() ?? '')) ?? c.emails[0];
          if (personal) setSendTo(personal);
          if (!local.trim()) setLocal((c.name.split(/\s+/)[0] ?? '').toLowerCase().replace(/[^a-z0-9._-]/g, ''));
        }} />
        <div>
          <div className="text-[12px] text-muted">Their new email address (also how they sign in)</div>
          <div className="mt-1 flex items-center gap-1.5">
            <Input value={local} onChange={(e) => setLocal(e.target.value.replace(/[@\s]/g, ''))} placeholder="makenzie" className="flex-1" />
            <span className="text-muted">@</span>
            <select value={d} onChange={(e) => setDomain(e.target.value)} className="h-9 rounded-xl border border-line bg-raised px-2 text-[13.5px]">
              {domains.map((x) => <option key={x}>{x}</option>)}
            </select>
          </div>
        </div>
        <label className="block text-[12px] text-muted">More addresses for the same mailbox (optional)<Input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="info@homesbymakenzie.com, sales@homesbymakenzie.com" /></label>
        <label className="block text-[12px] text-muted">Email the invitation to (optional — their current personal email)
          <Input value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="makenzie@gmail.com" />
        </label>
        {sendTo.trim() && <label className="block text-[12px] text-muted">Personal note (optional)<Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Here's your new work email!" /></label>}
        {canMakeAdmin && <label className="flex items-center gap-2 text-[13px]"><input type="checkbox" checked={admin} onChange={(e) => setAdmin(e.target.checked)} /> Make them an administrator (can invite people and manage domains)</label>}
        {address && (
          <AnimatePresence>
            <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 rounded-xl bg-bg/40 px-3 py-2 text-[12.5px] text-muted">
              <Favicon domain={d} size={16} /> {name || 'They'} will sign in as <b className="text-fg">{address}</b>
            </motion.div>
          </AnimatePresence>
        )}
      </div>
      <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={busy} disabled={!address} icon={<UserPlus className="size-4" />} onClick={submit}>{sendTo.trim() ? 'Create & send invitation' : 'Create invitation'}</Button>
      </div>
    </Modal>
  );
}

/** Name field that doubles as a contact search; picking a contact fills in the rest of the form. */
function ContactNameInput({ value, onChange, onPick }: { value: string; onChange: (v: string) => void; onPick: (c: Contact) => void }) {
  const contacts = useContacts().data?.contacts ?? [];
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const matches = useMemo(() => {
    const q = value.trim().toLowerCase();
    return contacts
      .filter((c) => !q || [c.name, c.company ?? '', ...c.emails].some((f) => f.toLowerCase().includes(q)))
      .sort((a, b) => Number(b.favorite) - Number(a.favorite) || a.name.localeCompare(b.name))
      .slice(0, 8);
  }, [contacts, value]);
  const pick = (c: Contact) => {
    onPick(c);
    setOpen(false);
  };

  return (
    <div className="relative">
      <label className="block text-[12px] text-muted">
        <span className="flex items-center justify-between">
          Their name
          {contacts.length > 0 && <span className="flex items-center gap-1 text-[11.5px] text-faint"><BookUser className="size-3" />Type to search your contacts</span>}
        </span>
        <Input
          autoFocus
          value={value}
          placeholder="Makenzie Giddens"
          onChange={(e) => (onChange(e.target.value), setOpen(true), setActive(0))}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={(e) => {
            if (!open || !matches.length) return;
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => (a + (e.key === 'ArrowDown' ? 1 : -1) + matches.length) % matches.length);
            } else if (e.key === 'Enter') {
              e.preventDefault();
              pick(matches[active]);
            } else if (e.key === 'Escape') {
              e.stopPropagation();
              setOpen(false);
            }
          }}
        />
      </label>
      {open && matches.length > 0 && (
        <div className="glass absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-xl bg-raised/95 p-1 shadow-2xl">
          {matches.map((c, i) => (
            <button
              key={c.id}
              onMouseDown={(e) => (e.preventDefault(), pick(c))}
              onMouseEnter={() => setActive(i)}
              className={cn('flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left', i === active && 'bg-line')}
            >
              <Avatar name={c.name} address={c.emails[0] ?? ''} size={28} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium">{c.name}{c.company ? <span className="font-normal text-muted"> · {c.company}</span> : null}</span>
                <span className="block truncate text-[11.5px] text-muted">{c.emails.join(', ') || 'No email on file'}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AddAddressDialog({ user, onClose, domains, onDone }: { user: User | null; onClose: () => void; domains: string[]; onDone: () => void }) {
  const [local, setLocal] = useState('');
  const [domain, setDomain] = useState('');
  const [busy, setBusy] = useState(false);
  const d = domain || domains[0] || '';
  return (
    <Modal open={!!user} onClose={onClose} width={460} title={user ? `Add an address for ${user.displayName}` : ''}>
      <div className="p-5">
        <div className="flex items-center gap-1.5">
          <Input autoFocus value={local} onChange={(e) => setLocal(e.target.value.replace(/[@\s]/g, ''))} placeholder="info" className="flex-1" />
          <span className="text-muted">@</span>
          <select value={d} onChange={(e) => setDomain(e.target.value)} className={cn('h-9 rounded-xl border border-line bg-raised px-2 text-[13.5px]')}>
            {domains.map((x) => <option key={x}>{x}</option>)}
          </select>
        </div>
        <p className="mt-2 text-[12px] text-muted">Mail to this address will arrive in {user?.displayName}'s inbox, and they can send from it.</p>
      </div>
      <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={busy} disabled={!local.trim() || !user} onClick={async () => {
          setBusy(true);
          try {
            await api.assignAddress(`${local.trim().toLowerCase()}@${d}`, user!.id, user!.displayName);
            setLocal('');
            onClose();
            onDone();
            toast.success('Address added');
          } catch (e) {
            toast.error(errorMessage(e));
          } finally {
            setBusy(false);
          }
        }}>Add address</Button>
      </div>
    </Modal>
  );
}
